import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Send, Check, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminInvoicesTab({ invoices, pricing, fetchAllData }) {
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    address: '',
    venue_count: 1,
    discount_percent: 0,
    notes: ''
  });
  const [processing, setProcessing] = useState(null);

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    setProcessing("invoice");
    try {
      await axios.post(`${API}/api/invoices`, invoiceForm);
      toast.success("Invoice created!");
      setShowInvoiceForm(false);
      setInvoiceForm({ company_name: '', contact_name: '', email: '', address: '', venue_count: 1, discount_percent: 0, notes: '' });
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create invoice");
    }
    setProcessing(null);
  };

  const handleUpdateInvoiceStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/api/invoices/${id}/status?status=${status}`);
      toast.success(`Invoice marked as ${status}`);
      fetchAllData();
    } catch (err) {
      toast.error("Failed to update invoice");
    }
  };

  const handleDeleteInvoice = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    try {
      await axios.delete(`${API}/api/invoices/${id}`);
      toast.success("Invoice deleted");
      fetchAllData();
    } catch (err) {
      toast.error("Failed to delete invoice");
    }
  };

  return (
    <div className="space-y-4">
      {/* Invoice Form Modal */}
      <AnimatePresence>
        {showInvoiceForm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white rounded-2xl p-6 w-full max-w-lg">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><FileText className="w-5 h-5 text-brand" /> Create Invoice</h3>
              <form onSubmit={handleCreateInvoice} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                    <input type="text" value={invoiceForm.company_name} onChange={(e) => setInvoiceForm({ ...invoiceForm, company_name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-200" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                    <input type="text" value={invoiceForm.contact_name} onChange={(e) => setInvoiceForm({ ...invoiceForm, contact_name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-200" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input type="email" value={invoiceForm.email} onChange={(e) => setInvoiceForm({ ...invoiceForm, email: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-200" required />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <input type="text" value={invoiceForm.address} onChange={(e) => setInvoiceForm({ ...invoiceForm, address: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-200" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Number of Venues</label>
                    <input type="number" min="1" value={invoiceForm.venue_count} onChange={(e) => setInvoiceForm({ ...invoiceForm, venue_count: parseInt(e.target.value) || 1 })} className="w-full px-3 py-2 rounded-lg border border-gray-200" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Discount %</label>
                    <input type="number" min="0" max="100" value={invoiceForm.discount_percent} onChange={(e) => setInvoiceForm({ ...invoiceForm, discount_percent: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 rounded-lg border border-gray-200" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea value={invoiceForm.notes} onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-200 h-20 resize-none" />
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex justify-between text-sm"><span>Subtotal ({invoiceForm.venue_count} × £{pricing.premium_annual}):</span><span>£{(invoiceForm.venue_count * pricing.premium_annual).toFixed(2)}</span></div>
                  {invoiceForm.discount_percent > 0 && <div className="flex justify-between text-sm text-green-600"><span>Discount ({invoiceForm.discount_percent}%):</span><span>-£{(invoiceForm.venue_count * pricing.premium_annual * invoiceForm.discount_percent / 100).toFixed(2)}</span></div>}
                  <div className="flex justify-between font-semibold text-lg border-t border-gray-200 pt-2 mt-2"><span>Total:</span><span>£{(invoiceForm.venue_count * pricing.premium_annual * (1 - invoiceForm.discount_percent / 100)).toFixed(2)}</span></div>
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={() => setShowInvoiceForm(false)} className="flex-1 btn-secondary">Cancel</button>
                  <button type="submit" disabled={processing === "invoice"} className="flex-1 btn-primary">{processing === "invoice" ? "Creating..." : "Create Invoice"}</button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Invoice Button */}
      <div className="flex justify-end">
        <button onClick={() => setShowInvoiceForm(true)} className="btn-primary flex items-center gap-2">
          <FileText className="w-4 h-4" /> Create Invoice
        </button>
      </div>

      {/* Invoice List */}
      {invoices.length === 0 ? (
        <div className="bg-white rounded-xl p-8 text-center">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="font-semibold text-gray-900">No invoices yet</h3>
          <p className="text-gray-500 mt-1">Create your first invoice for corporate clients.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b"><tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Venues</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {invoices.map((inv) => (
                <tr key={inv.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-brand">{inv.invoice_number}</td>
                  <td className="px-4 py-3"><p className="text-gray-900">{inv.company_name}</p><p className="text-xs text-gray-500">{inv.email}</p></td>
                  <td className="px-4 py-3 text-center">{inv.venue_count}</td>
                  <td className="px-4 py-3 text-right font-semibold">£{inv.total?.toFixed(2)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${inv.status === 'paid' ? 'bg-green-100 text-green-700' : inv.status === 'sent' ? 'bg-blue-100 text-blue-700' : inv.status === 'overdue' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>{inv.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {inv.status === 'draft' && <button onClick={() => handleUpdateInvoiceStatus(inv.id, 'sent')} className="p-1.5 text-blue-500 hover:bg-blue-50 rounded" title="Mark as Sent"><Send className="w-4 h-4" /></button>}
                      {(inv.status === 'sent' || inv.status === 'overdue') && <button onClick={() => handleUpdateInvoiceStatus(inv.id, 'paid')} className="p-1.5 text-green-500 hover:bg-green-50 rounded" title="Mark as Paid"><Check className="w-4 h-4" /></button>}
                      <button onClick={() => handleDeleteInvoice(inv.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
